/**
 * Passkey Service
 * Handles all passkey-related API calls for Beyond Identity integration
 */

import Storage from 'utils/localStorage/Storage';
import LOCAL_STORAGE from 'utils/localStorage/Constants';
import userUtils from 'utils/User';
import ufeApi from 'services/api/ufeApi';

const PASSKEY_BASE_URL = '/gway/v1/dotcom/auth';
const isSSR = () => typeof window === 'undefined' || (typeof Sephora !== 'undefined' && Sephora.isNodeRender);

const raisePasskeyError = message => {
    if (Sephora?.logger?.error) {
        Sephora.logger.error('[Passkey]', message);
    } else if (Sephora?.logger?.warn) {
        Sephora.logger.warn('[Passkey]', message);
    }

    throw new Error(message);
};

const getPasskeyConfig = () => {
    const config = Sephora?.configurationSettings?.passkey?.config;

    if (!config) {
        raisePasskeyError('Passkey configuration is missing.');
    }

    return config;
};

const ensureBrowser = () => {
    if (isSSR()) {
        raisePasskeyError('Passkey operations are unavailable during server-side rendering.');
    }
};

/**
 * Get access token from localStorage
 * Uses Storage utility which automatically handles JSON parsing and data extraction
 */
const getAccessToken = () => {
    try {
        return Storage.local.getItem(LOCAL_STORAGE.AUTH_ACCESS_TOKEN);
    } catch (error) {
        return null;
    }
};

/**
 * Create passkey binding
 * Calls Beyond Identity backend to create identity and get binding link
 * @param {string} email - User's email address (optional, will auto-detect from user profile)
 */
export const createPasskeyBinding = async (email = null) => {
    ensureBrowser();
    const accessToken = getAccessToken();

    if (!accessToken) {
        raisePasskeyError('No access token available. User must be logged in.');
    }

    const userEmail = email || userUtils.getProfileEmail();

    if (!userEmail) {
        raisePasskeyError('No user email found. Cannot create passkey binding.');
    }

    const data = await ufeApi.makeRequest(
        `${PASSKEY_BASE_URL}/passkeybinding`,
        {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json'
            },
            body: JSON.stringify({ username: userEmail })
        },
        {
            // Avoid triggering login modal when binding fails; surface error to caller instead
            skipLoginPrompt: true
        }
    );

    if (data?.responseStatus >= 400 || data?.errors?.length || data?.errorCode) {
        const message = data?.errors?.[0]?.errorMessage || data?.errorMessage || 'Passkey binding failed';

        raisePasskeyError(message);
    }

    return data;
};

// Beyond Identity Configuration imported from shared constants

/**
 * Get the configured redirect URI
 * @returns {string} The redirect URI
 */
export const getRedirectUri = () => {
    const { redirectUri } = getPasskeyConfig();

    if (!redirectUri) {
        raisePasskeyError('Passkey redirectUri is not configured.');
    }

    return redirectUri;
};

/**
 * Generate Beyond Identity authenticate URL by fetching from BI authorize endpoint
 * Note: PKCE is disabled for this application, so we don't include code_challenge
 * @param {string} stateValue - Hex-encoded SHA-256 digest used as state
 * @returns {Promise<{authenticateUrl: string, state: string}>}
 */
export const generateAuthenticateUrl = async stateValue => {
    ensureBrowser();
    const config = getPasskeyConfig();
    let baseUrl = config.baseUrl;

    // Replace template placeholders (e.g., ${config.region}) with actual config values
    const replacements = {
        '{{config.region}}': config.region,
        '{{config.tenantId}}': config.tenantId,
        '{{config.realmId}}': config.realmId,
        '{{config.applicationId}}': config.applicationId
    };

    Object.entries(replacements).forEach(([token, value]) => {
        if (baseUrl && baseUrl.includes(token)) {
            baseUrl = baseUrl.replaceAll(token, value);
        }
    });
    /* eslint-disable camelcase */
    const params = new URLSearchParams({
        response_type: 'code',
        client_id: config.clientId,
        redirect_uri: config.redirectUri,
        scope: 'openid',
        state: stateValue,
        // Request authenticate_url in JSON response (BI authorize supports include)
        include: 'authenticate_url'
    });
    /* eslint-enable camelcase */

    const authorizeUrl = `${baseUrl}?${params.toString()}`;
    const response = await fetch(authorizeUrl, { method: 'GET' });

    if (!response.ok) {
        const errorText = await response.text();

        raisePasskeyError(`Authorize endpoint failed: ${response.status} - ${errorText}`);
    }

    const data = await response.json();

    return {
        authenticateUrl: data.authenticate_url || authorizeUrl,
        state: stateValue
    };
};

/**
 * Parse redirect URL to extract authorization code and state
 * @param {string} redirectUrl - Redirect URL from authenticate()
 * @returns {Object} Parsed parameters {code, state, ...}
 */
export const parseRedirectUrl = redirectUrl => {
    const url = new URL(redirectUrl);
    const params = Object.fromEntries(Array.from(url.searchParams).map(([key, value]) => [key, value]));

    return params;
};

/**
 * Get account passkeys (account settings)
 * @returns {Promise<Object>} Passkeys payload from backend
 */
export const getPasskeys = async () => {
    ensureBrowser();
    const url = `${PASSKEY_BASE_URL}/passkeys`;

    const data = await ufeApi.makeRequest(
        url,
        { method: 'GET' },
        {
            // Do not surface sign-in modal on passkey fetch failures; caller will handle
            skipLoginPrompt: true
        }
    );

    if (data && data.errorCode) {
        raisePasskeyError(data.errorMessage || 'Failed to fetch passkeys');
    }

    return data;
};

/**
 * Remove an account passkey (account settings)
 * @param {string} passkeyId
 * @param {{firstName?: string, lastName?: string}} userData
 * @returns {Promise<Object>} Backend response
 */
export const removePasskey = async (passkeyId, userData) => {
    ensureBrowser();
    const url = `${PASSKEY_BASE_URL}/passkeys/${passkeyId}?firstName=${userData?.firstName}&lastName=${userData?.lastName}`;

    const data = await ufeApi.makeRequest(
        url,
        { method: 'DELETE' },
        {
            // Avoid triggering sign-in modal for passkey delete failures
            skipLoginPrompt: true
        }
    );

    if (data && data.errorCode) {
        raisePasskeyError(data.errorMessage || 'Failed to remove passkey');
    }

    return data;
};

/**
 * Create passkey metadata (account + device context)
 * @param {Object} input - Metadata payload
 * @param {string} input.passkeyId - Backend passkey identifier
 * @param {string} input.identityId - Beyond Identity identity ID
 * @param {string} input.deviceName - Friendly device/browser name
 * @param {string} input.email - User email
 * @param {string} [input.firstName] - User first name
 * @param {string} [input.lastName] - User last name
 * @returns {Promise<Object>} Backend response
 */
export const createPasskeyMetaData = async input => {
    ensureBrowser();
    const url = `${PASSKEY_BASE_URL}/passkeymetadata`;

    const data = await ufeApi.makeRequest(
        url,
        {
            method: 'POST',
            body: JSON.stringify(input),
            headers: {
                'Content-Type': 'application/json'
            }
        },
        {
            // Metadata errors should not prompt re-login
            skipLoginPrompt: true
        }
    );

    if (data && data.errorCode) {
        raisePasskeyError(data.errorMessage || 'Failed to create passkey metadata');
    }

    return data;
};

/**
 * Invalidate all passkeys for the current user
 * Used when password reset occurs and killswitch is enabled
 * @param {{firstName?: string, lastName?: string}} userData - User data for audit trail
 * @returns {Promise<{invalidatedCount: number, errors: Array}>} Result with count and any errors
 */
export const invalidateAllPasskeys = async userData => {
    ensureBrowser();

    try {
        // Fetch all passkeys for the account
        const passkeyResponse = await getPasskeys();

        // Convert object with numeric keys to array, filtering out metadata fields
        const passkeys = Object.keys(passkeyResponse)
            .filter(key => !isNaN(key)) // Only numeric keys (0, 1, 2, etc.)
            .map(key => passkeyResponse[key]);

        if (!passkeys.length) {
            return { invalidatedCount: 0, errors: [] };
        }

        // Delete each passkey
        const invalidationPromises = passkeys.map(async passkey => {
            try {
                await removePasskey(passkey.passkeyId, userData);

                return { success: true, passkeyId: passkey.passkeyId };
            } catch (error) {
                Sephora.logger?.error(`[Passkey] Failed to invalidate passkey ${passkey.passkeyId}`, {
                    error: error.message || error
                });

                return { success: false, passkeyId: passkey.passkeyId, error: error.message || error };
            }
        });

        const results = await Promise.allSettled(invalidationPromises);
        const invalidatedCount = results.filter(r => r.status === 'fulfilled' && r.value.success).length;
        const errors = results.filter(r => r.status === 'fulfilled' && !r.value.success).map(r => r.value);

        return { invalidatedCount, errors };
    } catch (error) {
        Sephora.logger?.error('[Passkey] Failed to invalidate passkeys', {
            error: error.message || error
        });

        return { invalidatedCount: 0, errors: [error.message || error] };
    }
};

export default {
    createPasskeyBinding,
    generateAuthenticateUrl,
    parseRedirectUrl,
    getRedirectUri,
    getPasskeys,
    removePasskey,
    createPasskeyMetaData,
    invalidateAllPasskeys
};
